"use client";

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import styles from './TopNav.module.scss';

/**
 * Global top navigation bar with responsive design and mobile hamburger menu
 */
export default function TopNav() {
  const [showSupportDropdown, setShowSupportDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSupportDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when window is resized to desktop
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setShowMobileMenu(false);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigationItems = [
    { href: "/overview", label: "Get Started" },
    { href: "/data-layers/core-data-fields", label: "Weather Layers" },
    { href: "/weather-forecast", label: "Weather Forecast" },
    { href: "/historical-weather", label: "Historical API" },
    { href: "/locations-api", label: "Monitoring" },
    { href: "/weather-maps", label: "Weather Map" },
  ];

  const supportItems = [
    { href: "https://www.tomorrow.io/a/faq/", label: "FAQ" },
    { href: "https://support.tomorrow.io/hc/en-us", label: "Help Center" },
    { href: "https://status.tomorrow.io/", label: "API Status" },
  ];

  return (
    <nav aria-label="Site" className={`${styles.topNav} flex-1 flex justify-start`} ref={mobileMenuRef}>
      {/* Desktop Navigation */}
      <ul className="hidden lg:flex items-center space-x-6 xl:space-x-6 lg:space-x-4 xl:px-6 lg:px-4 py-3 xl:text-base lg:text-sm">
        {navigationItems.map((item) => (
          <li key={item.href}>
            <Link 
              href={item.href} 
              className={`${styles.navigationLink}`}
            >
              {item.label}
            </Link>
          </li>
        ))}
        
        {/* Support - Dropdown */}
        <li 
          className="relative" 
          ref={dropdownRef}
          onMouseEnter={() => setShowSupportDropdown(true)}
          onMouseLeave={() => setShowSupportDropdown(false)}
        >
          <button
            className={`${styles.supportButton}`}
            aria-expanded={showSupportDropdown}
            aria-haspopup="true"
          >
            <span>Support</span>
            <svg
              className={`w-4 h-4 xl:w-4 xl:h-4 lg:w-3 lg:h-3 transition-transform duration-200 ${
                showSupportDropdown ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Desktop Dropdown Menu */}
          {showSupportDropdown && (
            <div className={`${styles.supportDropdown} absolute right-0 mt-0 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50`}>
              <div className="py-1">
                {supportItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.supportItem} block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary flex items-center justify-between transition-colors`}
                  >
                    <span>{item.label}</span>
                    <svg
                      className="w-3 h-3 ml-2 opacity-70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}
        </li>
      </ul>

      {/* Mobile Menu Button */}
      <div className="lg:hidden flex items-center justify-end flex-1">
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className={`${styles.mobileMenuButton} text-gray-700 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md`}
          aria-expanded={showMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {showMobileMenu ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className={`${styles.mobileMenu} lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50`}>
          <div className="px-6 py-4 space-y-4">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`${styles.mobileNavigationLink} block text-gray-700 hover:text-primary py-2 px-2 transition-colors`}
                onClick={() => setShowMobileMenu(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Support Section */}
            <div className="border-t border-gray-200 pt-4">
              <div className="text-sm font-medium text-gray-900 mb-2">Support</div>
              {supportItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.mobileNavigationLink} block text-gray-700 hover:text-primary py-2 pl-4 transition-colors flex items-center justify-between`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <span>{item.label}</span>
                  <svg
                    className="w-3 h-3 opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
} 